<script lang="ts">
  import type { ValidationSummary, ValidationIssue, IssueSeverity } from '../../core/validation/validation.types';
  import ErrorPanel from '../../components/ErrorPanel.svelte';

  export let validation: ValidationSummary;

  $: blockingIssues = validation.issues.filter((i) => i.severity === 'blocking');
  $: errorIssues = validation.issues.filter((i) => i.severity === 'error');
  $: warningIssues = validation.issues.filter((i) => i.severity === 'warning');
  $: infoIssues = validation.issues.filter((i) => i.severity === 'info');

  function toDisplaySeverity(s: IssueSeverity): 'error' | 'warning' | 'info' {
    if (s === 'blocking' || s === 'error') return 'error';
    if (s === 'warning') return 'warning';
    return 'info';
  }

  function toPanel(issues: ValidationIssue[]): Array<{ severity: 'error' | 'warning' | 'info'; message: string }> {
    return issues.map((i) => ({ severity: toDisplaySeverity(i.severity), message: i.message }));
  }

  let expandedSection: string | null = null;
  function toggleSection(name: string): void {
    expandedSection = expandedSection === name ? null : name;
  }
</script>

<div class="vs" aria-label="Resumo de validação">
  <!-- Confidence badge -->
  <div class="vs__header">
    <span class="vs__confidence vs__confidence--{validation.confidence}" aria-label="Confiança: {validation.confidence}">
      {#if validation.confidence === 'high'}
        Alta confiança
      {:else if validation.confidence === 'medium'}
        Média confiança
      {:else}
        Baixa confiança
      {/if}
    </span>

    <!-- Counter chips -->
    <div class="vs__counters" role="list" aria-label="Contagem de issues">
      {#if validation.blockingCount > 0}
        <span class="vs__counter vs__counter--blocking" role="listitem">
          {validation.blockingCount} bloqueante{validation.blockingCount !== 1 ? 's' : ''}
        </span>
      {/if}
      {#if validation.errorCount > 0}
        <span class="vs__counter vs__counter--error" role="listitem">
          {validation.errorCount} erro{validation.errorCount !== 1 ? 's' : ''}
        </span>
      {/if}
      {#if validation.warningCount > 0}
        <span class="vs__counter vs__counter--warning" role="listitem">
          {validation.warningCount} aviso{validation.warningCount !== 1 ? 's' : ''}
        </span>
      {/if}
      {#if validation.infoCount > 0}
        <span class="vs__counter vs__counter--info" role="listitem">
          {validation.infoCount} info
        </span>
      {/if}
    </div>
  </div>

  <!-- Blocking alert -->
  {#if validation.hasBlocking}
    <div class="vs__blocking-alert" role="alert">
      <strong>Atenção:</strong> Foram encontrados problemas críticos. O relatório não pode ser conclusivo até que sejam corrigidos.
    </div>
  {/if}

  <!-- Expandable issue sections -->
  {#if blockingIssues.length > 0}
    <div class="vs__section">
      <button
        class="vs__section-toggle vs__section-toggle--blocking"
        type="button"
        aria-expanded={expandedSection === 'blocking'}
        on:click={() => toggleSection('blocking')}
      >
        Problemas bloqueantes ({blockingIssues.length})
        <span class="vs__chevron" aria-hidden="true">{expandedSection === 'blocking' ? '▲' : '▼'}</span>
      </button>
      {#if expandedSection === 'blocking'}
        <div class="vs__section-body">
          <ErrorPanel issues={toPanel(blockingIssues)} />
        </div>
      {/if}
    </div>
  {/if}

  {#if errorIssues.length > 0}
    <div class="vs__section">
      <button
        class="vs__section-toggle vs__section-toggle--error"
        type="button"
        aria-expanded={expandedSection === 'error'}
        on:click={() => toggleSection('error')}
      >
        Erros ({errorIssues.length})
        <span class="vs__chevron" aria-hidden="true">{expandedSection === 'error' ? '▲' : '▼'}</span>
      </button>
      {#if expandedSection === 'error'}
        <div class="vs__section-body">
          <ErrorPanel issues={toPanel(errorIssues)} />
        </div>
      {/if}
    </div>
  {/if}

  {#if warningIssues.length > 0}
    <div class="vs__section">
      <button
        class="vs__section-toggle vs__section-toggle--warning"
        type="button"
        aria-expanded={expandedSection === 'warning'}
        on:click={() => toggleSection('warning')}
      >
        Avisos ({warningIssues.length})
        <span class="vs__chevron" aria-hidden="true">{expandedSection === 'warning' ? '▲' : '▼'}</span>
      </button>
      {#if expandedSection === 'warning'}
        <div class="vs__section-body">
          <ErrorPanel issues={toPanel(warningIssues)} />
        </div>
      {/if}
    </div>
  {/if}

  {#if infoIssues.length > 0}
    <div class="vs__section">
      <button
        class="vs__section-toggle vs__section-toggle--info"
        type="button"
        aria-expanded={expandedSection === 'info'}
        on:click={() => toggleSection('info')}
      >
        Informações ({infoIssues.length})
        <span class="vs__chevron" aria-hidden="true">{expandedSection === 'info' ? '▲' : '▼'}</span>
      </button>
      {#if expandedSection === 'info'}
        <div class="vs__section-body">
          <ErrorPanel issues={toPanel(infoIssues)} />
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .vs {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-3);
  }

  .vs__header {
    display: flex;
    align-items: center;
    gap: var(--ptt-space-3);
    flex-wrap: wrap;
  }

  .vs__confidence {
    display: inline-block;
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: var(--ptt-space-1) var(--ptt-space-3);
    border-radius: var(--ptt-radius-sm);
  }

  .vs__confidence--high {
    background: color-mix(in srgb, var(--ptt-green-100, #16a34a) 12%, var(--ptt-white));
    color: var(--ptt-green-100, #16a34a);
    border: 1px solid var(--ptt-green-100, #16a34a);
  }

  .vs__confidence--medium {
    background: color-mix(in srgb, var(--ptt-yellow-200) 15%, var(--ptt-white));
    color: var(--ptt-yellow-300, #b45309);
    border: 1px solid var(--ptt-yellow-200);
  }

  .vs__confidence--low {
    background: color-mix(in srgb, var(--ptt-orange-100) 10%, var(--ptt-white));
    color: var(--ptt-orange-100);
    border: 1px solid var(--ptt-orange-100);
  }

  .vs__counters {
    display: flex;
    gap: var(--ptt-space-2);
    flex-wrap: wrap;
  }

  .vs__counter {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 2px var(--ptt-space-2);
    border-radius: var(--ptt-radius-sm);
  }

  .vs__counter--blocking {
    background: color-mix(in srgb, var(--ptt-orange-100) 15%, var(--ptt-white));
    color: var(--ptt-orange-100);
  }

  .vs__counter--error {
    background: color-mix(in srgb, var(--ptt-orange-100) 10%, var(--ptt-white));
    color: var(--ptt-orange-100);
  }

  .vs__counter--warning {
    background: color-mix(in srgb, var(--ptt-yellow-200) 15%, var(--ptt-white));
    color: var(--ptt-yellow-300, #b45309);
  }

  .vs__counter--info {
    background: color-mix(in srgb, var(--ptt-blue-100) 10%, var(--ptt-white));
    color: var(--ptt-blue-100);
  }

  .vs__blocking-alert {
    padding: var(--ptt-space-3);
    border-left: 3px solid var(--ptt-orange-100);
    background: color-mix(in srgb, var(--ptt-orange-100) 8%, var(--ptt-white));
    font-size: 0.875rem;
    color: var(--ptt-gray-300);
    border-radius: 0 var(--ptt-radius-sm) var(--ptt-radius-sm) 0;
  }

  .vs__section {
    border: 1px solid var(--ptt-gray-20);
    border-radius: var(--ptt-radius-sm);
    overflow: hidden;
  }

  .vs__section-toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: var(--ptt-space-2) var(--ptt-space-3);
    background: var(--ptt-gray-10, #f5f5f5);
    border: none;
    font-family: var(--ptt-font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    text-align: left;
  }

  .vs__section-toggle:focus-visible {
    outline: none;
    box-shadow: var(--ptt-focus-ring);
  }

  .vs__section-toggle--blocking,
  .vs__section-toggle--error {
    color: var(--ptt-orange-100);
  }

  .vs__section-toggle--warning {
    color: var(--ptt-yellow-300, #b45309);
  }

  .vs__section-toggle--info {
    color: var(--ptt-blue-100);
  }

  .vs__chevron {
    font-size: 0.65rem;
    color: var(--ptt-gray-100);
  }

  .vs__section-body {
    padding: var(--ptt-space-3);
    background: var(--ptt-white);
  }
</style>
